import { authAPI } from "@/api/auth";
import BrandLogo from "@/assets/favicon.svg";
import { FormInputField } from "@/components/form-ui/form-input-field";
import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import { Header } from "@/components/ui/typography";
import { useAuth } from "@/provider/auth.provider";
import {
  defaultUserInviteVerifyFormValues,
  UserInviteVerifySchema,
  UserInviteVerifySchemaType,
} from "@/schemas/user-invite-verify-schema";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";

export function UserInviteVerifyPage() {
  const navigate = useNavigate();
  const { setPassword, loading, isAuthenticated } = useAuth();
  const [errorMessage, setErrorMessage] = useState(
    "Error verifying invite. Please try again."
  );
  const params = useParams<{ token: string }>();

  const form = useForm<UserInviteVerifySchemaType>({
    defaultValues: defaultUserInviteVerifyFormValues,
    resolver: zodResolver(UserInviteVerifySchema),
  });

  const {
    isError,
    isLoading: userInviteVerifyLoading,
    data,
    error,
  } = useQuery({
    queryKey: ["user-invite-verify"],
    queryFn: async () => {
      if (!params.token) {
        throw new Error("Invite token is required");
      }
      return authAPI.verifyInvite(params.token || "");
    },
  });

  const handleSubmit = (payload: UserInviteVerifySchemaType) => {
    // Handle form submission logic here
    console.log("Form submitted with payload:", payload);
    if (payload.password !== payload.confirmPassword) {
      form.setError("confirmPassword", {
        type: "validate",
        message: "Passwords do not match",
      });
      form.setError("password", {
        type: "validate",
        message: "Passwords do not match",
      });
    } else {
      console.log(params.token, payload);
      setPassword(params.token!, payload.password);
    }
  };

  useEffect(() => {
    console.log("user invite data --- ", userInviteVerifyLoading, data);
    if (!userInviteVerifyLoading && data) {
      form.setValue("email", data.email);
    }
  }, [userInviteVerifyLoading]);

  useEffect(() => {
    if (isAuthenticated) {
      navigate("/dashboard/companies", { replace: true });
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    if (isError && error instanceof AxiosError) {
      console.log("Error on verifying token -- ", error);
      console.log("error status --- ", error.response?.status);
      console.log("error data --- ", error.response?.data);
      const errorData = error.response?.data;

      if (errorData.statusCode === 400) {
        setErrorMessage("Invalid invite link.");
      }
    }
  }, [isError, error]);

  return (
    <div className="flex h-screen items-center justify-center bg-gray-50">
      <div className="max-w-md w-full p-6 bg-white rounded-lg shadow-md">
        <div className="flex flex-row justify-center">
          <div className="flex flex-row items-center mb-6">
            <img src={BrandLogo} alt="Disha Consultancy" className="h-8 mr-4" />
            <Header variant="h3">Disha Consultancy</Header>
          </div>
        </div>
        <h1 className="text-2xl font-semibold text-center mb-4">
          {userInviteVerifyLoading && !isError && "Verify Your Invitation"}
          {!userInviteVerifyLoading && data && "Set your password"}
        </h1>

        {userInviteVerifyLoading ? (
          <div className="w-full flex justify-center items-center">
            <Loader2 className="animate-spin mr-4" />
            Loading...
          </div>
        ) : (
          <div>
            {isError ? (
              <p className="text-red-500">{errorMessage}</p>
            ) : (
              <div>
                <Form {...form}>
                  <form
                    onSubmit={form.handleSubmit(handleSubmit)}
                    className="mt-8"
                  >
                    <FormInputField
                      fieldName="email"
                      label="Email"
                      control={form.control}
                      type="email"
                      className="mb-4"
                      disabled
                      defaultValue={data?.email || ""}
                    />
                    <FormInputField
                      fieldName="password"
                      label="Password"
                      control={form.control}
                      type="password"
                      className="mb-4"
                    />
                    <FormInputField
                      fieldName="confirmPassword"
                      label="Confirm Password"
                      control={form.control}
                      type="password"
                      className="mb-8"
                    />

                    {loading ? (
                      <Button type="button" className="w-full mt-8">
                        <Loader2 className="animate-spin mr-4" />
                        Setting Password...
                      </Button>
                    ) : (
                      <Button type="submit" className="w-full mt-8">
                        Set Password
                      </Button>
                    )}
                  </form>
                </Form>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
